import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SkuMappingGroup } from '../../components/dem/SkuMappingGroup';
import { TemplateListRow } from '../../components/dem/TemplateListRow';
import { Modal } from '../../components/ui/Modal';
import {
  createDemTemplate,
  deleteDemTemplate,
  fetchDemTemplates,
  fetchItemDescription,
  groupSkuMappingsByPdfKey,
  loadTemplateMappings,
  removeItemMapping,
  replaceItemMapping,
  saveDemTemplateConfig,
  upsertItemMapping,
} from '../../lib/demSkuMap';
import { useDemNotifications } from '../../contexts/DemNotificationsContext';
import { useDemMasterDataRealtime } from '../../hooks/useDemMasterDataRealtime';
import type { DemSkuMappingRow, DemTemplate } from '../../types/dem';

const MOBILE_MQ = '(max-width: 900px)';

type MappingModalMode = 'create' | 'edit' | 'remove';
type MappingSortKey = 'pdfKey' | 'sysCode' | 'factor' | 'uom' | 'description' | 'lastUsed';
type MappingSortDir = 'asc' | 'desc';

const MAPPING_SORT_COLUMNS: { key: MappingSortKey; label: string }[] = [
  { key: 'pdfKey', label: 'PDF SKU / text' },
  { key: 'sysCode', label: 'System SKU' },
  { key: 'factor', label: 'Factor' },
  { key: 'uom', label: 'UOM' },
  { key: 'description', label: 'Description' },
  { key: 'lastUsed', label: 'Last used' },
];

function compareMappingRows(
  a: DemSkuMappingRow,
  b: DemSkuMappingRow,
  key: MappingSortKey,
  dir: MappingSortDir,
): number {
  const av = String(a[key] ?? '');
  const bv = String(b[key] ?? '');
  const cmp = av.localeCompare(bv, undefined, { sensitivity: 'base', numeric: true });
  return dir === 'asc' ? cmp : -cmp;
}

export function DemSkuMapPage() {
  const [templateSearch, setTemplateSearch] = useState('');
  const [debouncedTemplateSearch, setDebouncedTemplateSearch] = useState('');
  const [templates, setTemplates] = useState<DemTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  const [selectedTemplateName, setSelectedTemplateName] = useState<string | null>(null);
  const [template, setTemplate] = useState<DemTemplate | null>(null);
  const [mappingRows, setMappingRows] = useState<DemSkuMappingRow[]>([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [mappingsError, setMappingsError] = useState<string | null>(null);

  const [selectedRow, setSelectedRow] = useState<DemSkuMappingRow | null>(null);
  const [editingRow, setEditingRow] = useState<DemSkuMappingRow | null>(null);
  const [pdfKey, setPdfKey] = useState('');
  const [sysCode, setSysCode] = useState('');
  const [factor, setFactor] = useState('1');
  const [descPreview, setDescPreview] = useState<string | null>(null);
  const [descPreviewState, setDescPreviewState] = useState<'idle' | 'found' | 'missing'>('idle');

  const [templateBusy, setTemplateBusy] = useState(false);
  const [mappingBusy, setMappingBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const { push: pushNotification } = useDemNotifications();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches,
  );
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  const [mappingModalMode, setMappingModalMode] = useState<MappingModalMode | null>(null);
  const [sortKey, setSortKey] = useState<MappingSortKey>('pdfKey');
  const [sortDir, setSortDir] = useState<MappingSortDir>('asc');
  const mappingsLoadGen = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const sync = () => {
      setIsMobile(mq.matches);
      if (!mq.matches) setRulesModalOpen(false);
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedTemplateSearch(templateSearch.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [templateSearch]);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const rows = await fetchDemTemplates(debouncedTemplateSearch);
      setTemplates(rows);
      setSelectedTemplateName((prev) => {
        if (!prev) return null;
        const stillThere = rows.some((row) => row.name === prev);
        if (!stillThere) setRulesModalOpen(false);
        return stillThere ? prev : null;
      });
    } catch (e) {
      setTemplatesError(e instanceof Error ? e.message : 'Failed to load templates.');
      setTemplates([]);
      setSelectedTemplateName(null);
      setRulesModalOpen(false);
    } finally {
      setTemplatesLoading(false);
    }
  }, [debouncedTemplateSearch]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const loadMappings = useCallback(async (name: string, opts?: { keepRows?: boolean }) => {
    const gen = ++mappingsLoadGen.current;
    setMappingsLoading(true);
    setMappingsError(null);
    // Drop stale rows when switching templates — otherwise the modal briefly
    // shows the previous code’s mappings (the “flash” when tapping another code).
    if (!opts?.keepRows) {
      setTemplate(null);
      setMappingRows([]);
    }
    try {
      const result = await loadTemplateMappings(name);
      if (gen !== mappingsLoadGen.current) return;
      setTemplate(result.template);
      setMappingRows(result.rows);
    } catch (e) {
      if (gen !== mappingsLoadGen.current) return;
      setMappingsError(e instanceof Error ? e.message : 'Failed to load mappings.');
      setTemplate(null);
      setMappingRows([]);
    } finally {
      if (gen === mappingsLoadGen.current) setMappingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedTemplateName) {
      setTemplate(null);
      setMappingRows([]);
      setMappingsError(null);
      return;
    }
    void loadMappings(selectedTemplateName);
  }, [selectedTemplateName, loadMappings]);

  useDemMasterDataRealtime({
    include: ['templates', 'ai_customer_mappings'],
    onChange: () => {
      void loadTemplates();
      // Soft refresh — keep current rows until the new payload arrives.
      if (selectedTemplateName) void loadMappings(selectedTemplateName, { keepRows: true });
    },
  });

  useEffect(() => {
    setSelectedRow(null);
    setEditingRow(null);
    setPdfKey('');
    setSysCode('');
    setFactor('1');
    setDescPreview(null);
    setDescPreviewState('idle');
    setExpandedGroups(new Set());
    setMappingModalMode(null);
  }, [selectedTemplateName]);

  const mappingGroups = useMemo(() => {
    const groups = groupSkuMappingsByPdfKey(mappingRows).map((group) => ({
      ...group,
      rows: [...group.rows].sort((a, b) => compareMappingRows(a, b, sortKey, sortDir)),
    }));

    groups.sort((ga, gb) => {
      if (sortKey === 'pdfKey') {
        const cmp = ga.pdfKey.localeCompare(gb.pdfKey, undefined, {
          sensitivity: 'base',
          numeric: true,
        });
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const a = ga.rows[0];
      const b = gb.rows[0];
      if (!a && !b) return 0;
      if (!a) return 1;
      if (!b) return -1;
      return compareMappingRows(a, b, sortKey, sortDir);
    });

    return groups;
  }, [mappingRows, sortKey, sortDir]);

  const duplicatePdfPairCount = useMemo(
    () => mappingRows.filter((row) => row.duplicatePdfText).length,
    [mappingRows],
  );

  const toggleSort = (key: MappingSortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir('asc');
  };

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    const code = sysCode.trim();
    if (!code) {
      setDescPreview(null);
      setDescPreviewState('idle');
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void fetchItemDescription(code).then((desc) => {
        if (cancelled) return;
        if (desc) {
          setDescPreview(desc);
          setDescPreviewState('found');
        } else {
          setDescPreview(null);
          setDescPreviewState('missing');
        }
      });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [sysCode]);

  const mappingCountLabel = useMemo(() => {
    if (!selectedTemplateName) return '';
    if (mappingsLoading) return 'Loading mappings…';
    return `${mappingRows.length.toLocaleString()} mapping${mappingRows.length === 1 ? '' : 's'}`;
  }, [selectedTemplateName, mappingsLoading, mappingRows.length]);

  const clearForm = () => {
    setEditingRow(null);
    setPdfKey('');
    setSysCode('');
    setFactor('1');
    setDescPreview(null);
    setDescPreviewState('idle');
  };

  const closeMappingModal = () => {
    setMappingModalMode(null);
    clearForm();
  };

  const openCreateModal = () => {
    if (!selectedTemplateName) {
      setStatusMessage('Select a template from the left list first.');
      return;
    }
    clearForm();
    setSelectedRow(null);
    setMappingModalMode('create');
  };

  const openEditModal = (row: DemSkuMappingRow) => {
    setSelectedRow(row);
    setEditingRow(row);
    setPdfKey(row.pdfKey);
    setSysCode(row.sysCode);
    setFactor(row.factor || '1');
    setExpandedGroups((prev) => {
      if (prev.has(row.pdfKey)) return prev;
      const next = new Set(prev);
      next.add(row.pdfKey);
      return next;
    });
    setMappingModalMode('edit');
  };

  const openRemoveModal = () => {
    if (!selectedTemplateName) {
      setStatusMessage('Select a template from the left list first.');
      return;
    }
    if (!selectedRow) {
      setStatusMessage('Select a mapping row first, then click Remove.');
      return;
    }
    setEditingRow(selectedRow);
    setPdfKey(selectedRow.pdfKey);
    setSysCode(selectedRow.sysCode);
    setFactor(selectedRow.factor || '1');
    setMappingModalMode('remove');
  };

  const selectTemplate = (name: string) => {
    if (name === selectedTemplateName) {
      if (isMobile) setRulesModalOpen(true);
      return;
    }
    // Clear immediately (before effect) so the sheet never paints the old list.
    setTemplate(null);
    setMappingRows([]);
    setMappingsError(null);
    setMappingsLoading(true);
    setSelectedTemplateName(name);
    if (isMobile) setRulesModalOpen(true);
  };

  const selectMappingRow = (row: DemSkuMappingRow) => {
    setSelectedRow(row);
    setExpandedGroups((prev) => {
      if (prev.has(row.pdfKey)) return prev;
      const next = new Set(prev);
      next.add(row.pdfKey);
      return next;
    });
  };

  const handleCreateTemplate = async () => {
    const name = window.prompt('Enter customer / template name:');
    if (!name?.trim()) return;

    setTemplateBusy(true);
    setStatusMessage(null);
    try {
      const created = await createDemTemplate(name);
      await loadTemplates();
      setSelectedTemplateName(created.name);
      if (isMobile) setRulesModalOpen(true);
      setStatusMessage(`Created template: ${created.name}`);
    } catch (e) {
      setStatusMessage(e instanceof Error ? e.message : 'Failed to create template.');
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplateName) return;
    if (
      !window.confirm(
        `Permanently delete template "${selectedTemplateName}"?\n\nThis cannot be undone.`,
      )
    ) {
      return;
    }

    setTemplateBusy(true);
    setStatusMessage(null);
    try {
      await deleteDemTemplate(selectedTemplateName);
      setSelectedTemplateName(null);
      setRulesModalOpen(false);
      await loadTemplates();
      setStatusMessage(`Template "${selectedTemplateName}" deleted.`);
    } catch (e) {
      setStatusMessage(e instanceof Error ? e.message : 'Failed to delete template.');
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleSaveMapping = async () => {
    if (!selectedTemplateName || !template) {
      setStatusMessage('Select a template from the left list first.');
      return;
    }

    const trimmedPdf = pdfKey.trim();
    const trimmedSys = sysCode.trim();
    const trimmedFactor = factor.trim() || '1';

    if (!trimmedPdf || !trimmedSys) {
      setStatusMessage('PDF text and System SKU are required.');
      return;
    }

    setMappingBusy(true);
    setStatusMessage(null);
    try {
      const nextConfig =
        mappingModalMode === 'edit' && editingRow
          ? replaceItemMapping(
              template.config,
              {
                pdfKey: editingRow.pdfKey,
                sysCode: editingRow.sysCode,
                factor: editingRow.factor,
                uom: editingRow.uom,
              },
              { pdfKey: trimmedPdf, sysCode: trimmedSys, factor: trimmedFactor },
            )
          : upsertItemMapping(template.config, trimmedPdf, trimmedSys, trimmedFactor);
      const saved = await saveDemTemplateConfig(selectedTemplateName, nextConfig);
      setTemplate(saved);
      await loadMappings(selectedTemplateName);
      closeMappingModal();
      setSelectedRow(null);
      setStatusMessage(mappingModalMode === 'edit' ? 'Mapping updated.' : 'Mapping saved.');
    } catch (e) {
      setStatusMessage(e instanceof Error ? e.message : 'Failed to save mapping.');
    } finally {
      setMappingBusy(false);
    }
  };

  const handleRemoveMapping = async () => {
    if (!selectedTemplateName || !template) return;

    const target = editingRow ?? selectedRow;
    if (!target) {
      setStatusMessage('Select a mapping row first.');
      return;
    }

    setMappingBusy(true);
    setStatusMessage(null);
    try {
      const nextConfig = removeItemMapping(
        template.config,
        target.pdfKey,
        target.sysCode,
        target.factor,
        target.uom,
      );
      const saved = await saveDemTemplateConfig(selectedTemplateName, nextConfig);
      setTemplate(saved);
      await loadMappings(selectedTemplateName);
      closeMappingModal();
      setSelectedRow(null);
      setStatusMessage('Mapping removed.');
      pushNotification({
        tone: 'ok',
        title: 'Mapping removed.',
      });
    } catch (e) {
      setStatusMessage(e instanceof Error ? e.message : 'Failed to remove mapping.');
    } finally {
      setMappingBusy(false);
    }
  };

  const mappingToolbar = selectedTemplateName ? (
    <div className="dem-sku-map-rules__toolbar">
      <button
        type="button"
        className="btn-primary btn-sm"
        disabled={mappingBusy || mappingsLoading}
        onClick={openCreateModal}
      >
        Create
      </button>
      <button
        type="button"
        className="btn-secondary btn-sm dem-sku-map-btn-remove"
        disabled={mappingBusy || mappingsLoading || !selectedRow}
        onClick={openRemoveModal}
      >
        Remove
      </button>
    </div>
  ) : null;

  const rulesBody = selectedTemplateName ? (
    <>
      {mappingsError && <p className="alert alert-error">{mappingsError}</p>}

      {duplicatePdfPairCount > 0 && (
        <p className="alert alert-warn dem-sku-map-dup-banner" role="status">
          {duplicatePdfPairCount} row{duplicatePdfPairCount === 1 ? '' : 's'} have duplicate PDF SKU /
          text + System SKU pairs (highlighted). Edit or remove extras so each pair is unique.
        </p>
      )}

      <div className="dem-sku-map-table-wrap">
        <div className="dem-sku-map-table__head" role="row">
          {MAPPING_SORT_COLUMNS.map((col) => {
            const active = sortKey === col.key;
            return (
              <button
                key={col.key}
                type="button"
                className={`dem-sku-map-table__sort${active ? ' dem-sku-map-table__sort--active' : ''}`}
                onClick={() => toggleSort(col.key)}
                aria-label={`Sort by ${col.label} ${active && sortDir === 'asc' ? 'descending' : 'ascending'}`}
              >
                <span>{col.label}</span>
                <span className="dem-sku-map-table__sort-icon" aria-hidden>
                  {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                </span>
              </button>
            );
          })}
        </div>

        <div className="dem-sku-map-table__body">
          {mappingsLoading && mappingRows.length === 0 ? (
            <p className="dem-sku-map-empty">Loading mappings…</p>
          ) : mappingRows.length === 0 ? (
            <p className="dem-sku-map-empty">No mappings yet for this template.</p>
          ) : (
            mappingGroups.map((group) => (
              <SkuMappingGroup
                key={group.pdfKey}
                group={group}
                expanded={expandedGroups.has(group.pdfKey)}
                onToggle={toggleGroup}
                selectedRow={selectedRow}
                onSelectRow={selectMappingRow}
                onEditRow={openEditModal}
              />
            ))
          )}
        </div>
      </div>
    </>
  ) : (
    <div className="dem-sku-map-empty dem-sku-map-empty--center">
      Select a template on the left to view and edit SKU mappings.
    </div>
  );

  const mappingModalTitle =
    mappingModalMode === 'edit'
      ? 'Edit mapping'
      : mappingModalMode === 'remove'
        ? 'Remove mapping'
        : 'Create mapping';

  return (
    <div className="dem-sku-map-page dem-sku-map-page--floating">
      <div className="dem-floating-page-actions dem-floating-page-actions--end">
        <div className="dem-floating-page-actions__btns">
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={templatesLoading || mappingsLoading || templateBusy || mappingBusy}
            onClick={() => {
              void loadTemplates();
              if (selectedTemplateName) void loadMappings(selectedTemplateName, { keepRows: true });
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {statusMessage && !(isMobile && rulesModalOpen) && (
        <p className="dem-uid-status dem-uid-status--ok" role="status">
          {statusMessage}
        </p>
      )}

      <div className="dem-sku-map-layout">
        <div className="dem-sku-map-templates card">
          <div className="dem-sku-map-panel__head">
            <h2 className="dem-sku-map-panel__title">Templates</h2>
            <span className="dem-sku-map-count-badge">
              {templatesLoading ? '…' : `${templates.length}`}
            </span>
          </div>

          <div className="dem-sku-map-search-wrap">
            <input
              type="text"
              inputMode="search"
              autoComplete="off"
              className="field-input dem-sku-map-search"
              placeholder="Search templates"
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
            />
          </div>

          {templatesError && <p className="alert alert-error">{templatesError}</p>}

          <div className="dem-sku-map-template-list">
            {templatesLoading && templates.length === 0 ? (
              <p className="dem-sku-map-empty">Loading templates…</p>
            ) : templates.length === 0 ? (
              <p className="dem-sku-map-empty">No templates match your search.</p>
            ) : (
              templates.map((tmpl) => (
                <TemplateListRow
                  key={tmpl.name}
                  name={tmpl.name}
                  selected={selectedTemplateName === tmpl.name}
                  onSelect={selectTemplate}
                />
              ))
            )}
          </div>

          <div className="dem-sku-map-template-actions">
            <button
              type="button"
              className="btn-primary btn-sm dem-sku-map-btn-new"
              disabled={templateBusy}
              onClick={() => void handleCreateTemplate()}
            >
              New template
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm dem-sku-map-btn-delete"
              disabled={templateBusy || !selectedTemplateName}
              onClick={() => void handleDeleteTemplate()}
            >
              Delete
            </button>
          </div>
        </div>

        {!isMobile && (
          <div className="dem-sku-map-rules card dem-sku-map-rules--inline">
            <div className="dem-sku-map-panel__head">
              <h2 className="dem-sku-map-panel__title">
                {selectedTemplateName ? `Mapping rules · ${selectedTemplateName}` : 'Mapping rules'}
              </h2>
              <div className="dem-sku-map-panel__head-actions">
                {selectedTemplateName && (
                  <span className="dem-sku-map-count-badge">{mappingCountLabel}</span>
                )}
                {mappingToolbar}
              </div>
            </div>
            {rulesBody}
          </div>
        )}
      </div>

      <Modal
        title={
          selectedTemplateName
            ? `Mapping rules · ${selectedTemplateName}`
            : 'Mapping rules'
        }
        open={isMobile && rulesModalOpen && Boolean(selectedTemplateName)}
        onClose={() => setRulesModalOpen(false)}
        fullScreen
        compactHeader
        panelClassName="dem-sku-map-rules-modal"
      >
        <div className="dem-sku-map-rules-modal__head">
          <span className="dem-sku-map-count-badge">{mappingCountLabel}</span>
          {mappingToolbar}
        </div>
        {statusMessage && (
          <p className="dem-uid-status dem-uid-status--ok dem-sku-map-rules-modal__status" role="status">
            {statusMessage}
          </p>
        )}
        <div className="dem-sku-map-rules dem-sku-map-rules--modal">{rulesBody}</div>
      </Modal>

      <Modal
        title={mappingModalTitle}
        open={mappingModalMode != null}
        onClose={closeMappingModal}
        compactHeader
        panelClassName="dem-sku-map-mapping-modal"
      >
        <div className="dem-sku-map-editor dem-sku-map-editor--modal">
          <div className="dem-sku-map-editor__grid">
            <label className="dem-sku-map-field">
              <span>PDF text</span>
              <input
                className="field-input"
                value={pdfKey}
                onChange={(e) => setPdfKey(e.target.value)}
                placeholder="Text extracted from PDF"
                disabled={mappingModalMode === 'remove' || mappingBusy}
              />
            </label>

            <label className="dem-sku-map-field">
              <span>System SKU</span>
              <input
                className="field-input"
                value={sysCode}
                onChange={(e) => setSysCode(e.target.value)}
                placeholder="Item code"
                disabled={mappingModalMode === 'remove' || mappingBusy}
              />
            </label>

            <label className="dem-sku-map-field dem-sku-map-field--factor">
              <span>Factor</span>
              <input
                className="field-input"
                value={factor}
                onChange={(e) => setFactor(e.target.value)}
                placeholder="1"
                disabled={mappingModalMode === 'remove' || mappingBusy}
              />
            </label>
          </div>

          {mappingModalMode !== 'remove' ? (
            <p
              className={`dem-sku-map-desc-preview${
                descPreviewState === 'found'
                  ? ' dem-sku-map-desc-preview--found'
                  : descPreviewState === 'missing'
                    ? ' dem-sku-map-desc-preview--missing'
                    : ''
              }`}
            >
              {descPreviewState === 'idle' && 'Waiting for System SKU…'}
              {descPreviewState === 'found' && `Found: ${descPreview}`}
              {descPreviewState === 'missing' && 'Code not found in Item Master'}
            </p>
          ) : (
            <p className="dem-sku-map-desc-preview">
              Confirm removal of this mapping from the template.
            </p>
          )}

          <div className="dem-sku-map-editor__actions">
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={mappingBusy}
              onClick={closeMappingModal}
            >
              Cancel
            </button>
            {mappingModalMode === 'remove' ? (
              <button
                type="button"
                className="btn-primary btn-sm dem-sku-map-btn-remove"
                disabled={mappingBusy}
                onClick={() => void handleRemoveMapping()}
              >
                {mappingBusy ? 'Removing…' : 'Remove mapping'}
              </button>
            ) : (
              <button
                type="button"
                className="btn-primary btn-sm"
                disabled={mappingBusy}
                onClick={() => void handleSaveMapping()}
              >
                {mappingBusy
                  ? 'Saving…'
                  : mappingModalMode === 'edit'
                    ? 'Save changes'
                    : 'Save mapping'}
              </button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
